import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Where a choice's selection mark sits (DESIGN-SYSTEM §7.3, §15): one mark that moves from the old choice to the
 * new, instead of one choice's fill fading out while the next one's fades in.
 *
 * The track is the positioned element holding the choices; the chosen one carries `data-active="true"`. The mark
 * is measured from it (left and width, so choices of different widths work), and measured again when the track
 * changes size (a font arriving, a narrower window). The first measurement is placed without movement — nothing
 * slides in from the edge as a page opens — and every later one moves. `animate` is false until then.
 */
export function useIndicator<T extends HTMLElement>(active: string | null) {
    const track = useRef<T>(null);
    const [box, setBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
    const [animate, setAnimate] = useState(false);
    useLayoutEffect(() => {
        const el = track.current;
        if (!el) return;
        const measure = () => {
            const on = el.querySelector<HTMLElement>('[data-active="true"]');
            setBox(prev => {
                if (!on) return null;
                const next = { left: on.offsetLeft, top: on.offsetTop, width: on.offsetWidth, height: on.offsetHeight };
                return prev && prev.left === next.left && prev.top === next.top && prev.width === next.width && prev.height === next.height ? prev : next;
            });
        };
        measure();
        // A row that scrolls sideways (tabs on a phone) brings the new choice into view — only when the choice
        // changes, never on a resize, so a row somebody scrolled by hand stays where they left it.
        const on = el.querySelector<HTMLElement>('[data-active="true"]');
        if (on && el.scrollWidth > el.clientWidth) {
            const start = on.offsetLeft - 16, end = on.offsetLeft + on.offsetWidth + 16;
            if (start < el.scrollLeft) el.scrollLeft = Math.max(0, start);
            else if (end > el.scrollLeft + el.clientWidth) el.scrollLeft = end - el.clientWidth;
        }
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        observer?.observe(el);
        for (const child of Array.from(el.children)) observer?.observe(child);
        return () => observer?.disconnect();
    }, [active]);
    // Movement only once the mark has been placed: two frames, so the first placement is painted still.
    useLayoutEffect(() => {
        if (animate || !box || typeof requestAnimationFrame === 'undefined') return;
        let inner = 0;
        const outer = requestAnimationFrame(() => { inner = requestAnimationFrame(() => setAnimate(true)); });
        return () => { cancelAnimationFrame(outer); cancelAnimationFrame(inner); };
    }, [animate, box]);
    const style: React.CSSProperties | undefined = box
        ? { transform: `translateX(${box.left}px)`, width: box.width }
        : undefined;
    // `box` is for a mark that also moves between rows (choices that wrap onto two lines on a phone).
    return { track, style, box, shown: box !== null, animate };
}

/**
 * Arrow keys across a row of choices that act as one control: Left and Right (and Home and End) choose the
 * neighbour and move the focus with it, as a native radio group does.
 */
export function arrowChoice<T>(values: readonly T[], value: T, choose: (v: T) => void, focus: (v: T) => void) {
    return (e: React.KeyboardEvent) => {
        const at = values.indexOf(value);
        const to = e.key === 'ArrowRight' ? at + 1 : e.key === 'ArrowLeft' ? at - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? values.length - 1 : null;
        if (to === null) return;
        e.preventDefault();
        const next = values[(to + values.length) % values.length]!;
        choose(next);
        focus(next);
    };
}
